import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, Shield, Check, Wallet } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import GoogleAdsSettingsCard from '../components/settings/GoogleAdsSettingsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [financeToggling, setFinanceToggling] = useState(false);

  const handleToggleFinance = async (enabled) => {
    setFinanceToggling(true);
    try {
      await axios.put(`${API}/users/me/finance-access`, { enabled }, { withCredentials: true });
      await checkAuth();  // refresh user.has_finance_access in context
      toast.success(enabled ? "Accès Finance activé" : "Accès Finance désactivé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setFinanceToggling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);

    if (form.new_password.length < 6) {
      toast.error('Le nouveau mot de passe doit faire au moins 6 caracteres');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings/password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      }, { withCredentials: true });
      toast.success('Mot de passe modifie avec succes');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Parametres</h1>
          <p className="text-neutral-500 mt-1">Gerez votre compte et vos preferences</p>
        </div>

        {/* User info */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#002FA7] flex items-center justify-center">
              <span className="text-white font-heading font-bold text-xl">
                {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </span>
            </div>
            <div>
              <p className="font-heading font-semibold text-lg text-neutral-900">{user?.name}</p>
              <p className="text-neutral-500 text-sm">{user?.email}</p>
              <span className="inline-flex items-center gap-1 mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                <Shield className="w-3 h-3" />
                {user?.role === 'admin' ? 'Administrateur' : user?.role === 'admin_finance' ? 'Admin Finance' : 'Consultant'}
              </span>
            </div>
          </div>
        </div>

        {/* Finance access toggle — admin only */}
        {user?.role === 'admin' && (
          <div className="bg-white border border-emerald-200 rounded-lg p-6" data-testid="finance-access-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                  <Wallet className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-lg text-neutral-900">Accès au module Résultats (Finance)</h2>
                  <p className="text-sm text-neutral-500 mt-1">
                    Activez pour voir le tableau de bord financier (facturation, encaissements, marge entreprise).
                    Vous conservez vos droits d'administrateur. Le rôle « Admin Finance » dédié reste disponible
                    pour déléguer cet accès à un comptable sans lui donner les autres droits admin.
                  </p>
                </div>
              </div>
              <Switch
                checked={!!user.has_finance_access}
                disabled={financeToggling}
                onCheckedChange={handleToggleFinance}
                data-testid="toggle-finance-access"
              />
            </div>
          </div>
        )}

        {/* Password change form */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-neutral-700" />
            <h2 className="font-heading font-semibold text-lg text-neutral-900">Changer le mot de passe</h2>
          </div>

          {success && (
            <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="password-success-msg">
              <Check className="w-4 h-4" />
              Mot de passe modifie avec succes
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current_password">Mot de passe actuel</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrent ? 'text' : 'password'}
                  value={form.current_password}
                  onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                  required
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  onClick={() => setShowCurrent(!showCurrent)} tabIndex={-1}>
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new_password">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNew ? 'text' : 'password'}
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  onClick={() => setShowNew(!showNew)} tabIndex={-1}>
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-neutral-400">Minimum 6 caracteres</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm_password"
                type="password"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="bg-[#002FA7] hover:bg-[#002277] w-full"
              data-testid="change-password-btn"
            >
              {saving ? 'Modification...' : 'Modifier le mot de passe'}
            </Button>
          </form>
        </div>

        {/* Google Ads (Admin only) */}
        {user?.role === 'admin' && <GoogleAdsSettingsCard />}
      </div>
    </Layout>
  );
}
